import React, { Fragment, ReactElement, ReactNode } from 'react';
import styled from 'styled-components';
import { useMediaQuery } from '@mui/material';
import {
  RecordColumn,
  RecordRowPresentation,
} from '../../../common/model/ui/record-list.type';
import { OrderBy } from '../../../common/types';
import breakpoints from '../../../design-system/breakpoints';
import OvRecordTable from '../atoms/OvRecordTable';
import OvSortableColumnHeader from '../atoms/OvSortableColumnHeader';
import OvVisuallyHidden from '../atoms/OvVisuallyHidden';
import OvRecordListCard from '../molecules/OvRecordListCard';
import OvRecordListRow from '../molecules/OvRecordListRow';

interface OvRecordListProps<T> {
  label: string;
  records: T[];
  columns: RecordColumn<T>[];
  getKey: (record: T) => string;
  getDetailsPath?: (record: T) => string;
  getNavigationState?: () => unknown;
  getRowPresentation?: (record: T) => RecordRowPresentation;
  renderCard?: (record: T, detailsPath?: string) => ReactNode;
  orderBy?: string;
  orderDir?: string;
  onSort?: (order: OrderBy) => void;
}

const OvRecordList = <T,>({
  label,
  records,
  columns,
  getKey,
  getDetailsPath,
  getNavigationState,
  getRowPresentation,
  renderCard,
  orderBy = '',
  orderDir = '',
  onSort,
}: OvRecordListProps<T>): ReactElement => {
  const isTableLayout = useMediaQuery(`(min-width:${breakpoints.lg})`);

  if (!isTableLayout) {
    return (
      <Cards aria-label={label}>
        {records.map((record) => {
          const detailsPath = getDetailsPath?.(record);

          return (
            <Fragment key={getKey(record)}>
              {renderCard ? (
                renderCard(record, detailsPath)
              ) : (
                <OvRecordListCard
                  record={record}
                  columns={columns}
                  detailsPath={detailsPath}
                  getNavigationState={getNavigationState}
                  presentation={getRowPresentation?.(record)}
                />
              )}
            </Fragment>
          );
        })}
      </Cards>
    );
  }

  const renderHeader = (column: RecordColumn<T>) => {
    if (column.sortKey && onSort) {
      return (
        <OvSortableColumnHeader
          key={column.id}
          label={column.header}
          sortBy={column.sortKey}
          activeOrderBy={orderBy}
          activeOrderDir={orderDir}
          onSort={onSort}
        />
      );
    }

    return (
      <Header key={column.id} scope="col" $align={column.align ?? 'left'}>
        {column.isHeaderHidden ? (
          <OvVisuallyHidden>{column.header}</OvVisuallyHidden>
        ) : (
          column.header
        )}
      </Header>
    );
  };

  return (
    <OvRecordTable label={label}>
      <thead>
        <tr>{columns.map(renderHeader)}</tr>
      </thead>
      <tbody>
        {records.map((record) => (
          <OvRecordListRow
            key={getKey(record)}
            record={record}
            columns={columns}
            detailsPath={getDetailsPath?.(record)}
            getNavigationState={getNavigationState}
            presentation={getRowPresentation?.(record)}
          />
        ))}
      </tbody>
    </OvRecordTable>
  );
};

export default OvRecordList;

const Cards = styled.ul`
  margin: 0;
  padding: 0;
`;

const Header = styled.th<{ $align: 'left' | 'right' }>`
  && {
    text-align: ${({ $align }) => $align};
  }
`;
